import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert, RefreshCw, Lock, TrendingUp, AlertTriangle, Check, Mail, Download, Share2, Copy, XCircle, Users, Target, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { TraitBar } from "@/components/portal/Primitives";
import { portalApi, downloadReportPdf } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { track } from "@/lib/analytics";
import { BIG_FIVE_TRAITS, DARK_TRIAD_TRAITS, TRAIT_LABELS, BEHAVIOR_LABELS, SEVERITY_STYLES } from "@/data/questionnaires";

const TrendChart = ({ trends }) => {
  const max = Math.max(1, ...trends.trend_data.map((w) => w.count));
  return (
    <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8" data-testid="report-trends">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-2">Incident frequency</p>
          <p className="font-serif text-2xl text-white">{trends.total_incidents} logged</p>
        </div>
        <span
          className={`flex items-center gap-1.5 text-xs font-mono ${
            trends.change_percentage > 0 ? "text-red-400" : "text-emerald-400"
          }`}
        >
          <TrendingUp size={13} />
          {trends.change_percentage > 0 ? "+" : ""}
          {trends.change_percentage}%
        </span>
      </div>
      <div className="flex items-end gap-2 h-28">
        {trends.trend_data.map((w) => (
          <div key={w.week} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end h-full">
              <div
                className="w-full bg-[#D4AF37]/70 transition-all duration-700 min-h-[2px]"
                style={{ height: `${(w.count / max) * 100}%` }}
                title={`${w.count} incidents`}
              />
            </div>
            <span className="text-[9px] uppercase tracking-wider text-slate-600">{w.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const VulnerabilityCard = ({ vuln, index, premium }) => {
  const style = SEVERITY_STYLES[vuln.severity] || SEVERITY_STYLES.moderate;
  return (
    <div
      className="border bg-[#0B1220]/80 p-6 md:p-8"
      style={{ borderColor: style.border, animation: `fadeInUp 0.4s ease-out ${index * 90}ms both` }}
      data-testid={`vulnerability-card-${index}`}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span
          className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1"
          style={{ color: style.color, backgroundColor: style.bg, border: `1px solid ${style.border}` }}
          data-testid={`vulnerability-severity-${index}`}
        >
          {style.label}
        </span>
        <span className="font-mono text-[11px] text-slate-500">{vuln.trait_interaction}</span>
      </div>
      <h3 className="font-serif text-xl md:text-2xl text-white mb-4">{vuln.label}</h3>
      <p className="text-slate-400 text-sm leading-relaxed mb-6">{vuln.description}</p>

      {vuln.linked_behaviors?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {vuln.linked_behaviors.map((b) => (
            <span key={b} className="text-[10px] uppercase tracking-widest border border-slate-800 text-slate-500 px-2 py-1">
              {BEHAVIOR_LABELS[b] || b}
            </span>
          ))}
        </div>
      )}

      <div className="border-t border-slate-800 pt-5">
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Counter-strategies</p>
        {premium ? (
          <ul className="space-y-3">
            {vuln.counter_strategies.map((s) => (
              <li key={s} className="flex gap-3 text-slate-300 text-sm leading-relaxed">
                <Check size={14} className="text-[#D4AF37] shrink-0 mt-1" />
                {s}
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 border border-slate-800 p-4">
            <p className="flex items-center gap-2.5 text-slate-400 text-sm">
              <Lock size={14} className="text-[#D4AF37]" />
              Personalised counter-strategies are part of Premium
            </p>
            <Link
              to="/portal/pricing"
              className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] hover:underline shrink-0"
              data-testid={`vulnerability-upgrade-${index}`}
            >
              Unlock →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const SharePanel = ({ report, premium }) => {
  const [shareUrl, setShareUrl] = useState(null);
  const [includeStrategies, setIncludeStrategies] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revoked, setRevoked] = useState(false);

  const createLink = async (withStrategies) => {
    setBusy(true);
    try {
      const res = await portalApi.shareReport(withStrategies);
      setShareUrl(`${window.location.origin}${res.share_path}`);
      setRevoked(false);
      toast.success("Share link ready");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await portalApi.revokeShare();
      setShareUrl(null);
      setRevoked(true);
      toast.success("Link revoked — it no longer opens");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select the link and copy it manually");
    }
  };

  return (
    <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8" data-testid="report-share-panel">
      <div className="flex items-center gap-2.5 mb-4">
        <Share2 size={15} className="text-[#D4AF37]" />
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Share with a trusted colleague</p>
      </div>
      <p className="text-slate-400 text-sm leading-relaxed max-w-2xl mb-6">
        Creates a read-only link to a redacted copy — your name, email and the raw incident log are stripped out. Only
        the trait profiles, the vulnerabilities and the escalation risk are shown. You can revoke it at any time.
      </p>

      {premium && (
        <label className="flex items-center gap-3 mb-6 cursor-pointer select-none w-fit">
          <input
            type="checkbox"
            checked={includeStrategies}
            onChange={(e) => setIncludeStrategies(e.target.checked)}
            data-testid="share-include-strategies"
            className="w-4 h-4 accent-[#D4AF37]"
          />
          <span className="text-slate-300 text-sm">Also include my counter-strategies</span>
        </label>
      )}

      {shareUrl ? (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              readOnly
              value={shareUrl}
              data-testid="share-url-input"
              className="flex-1 bg-slate-900 border border-slate-800 px-4 py-3 text-slate-300 text-sm font-mono"
            />
            <button
              onClick={copy}
              data-testid="share-copy-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-slate-950 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
            >
              <Copy size={13} /> Copy
            </button>
          </div>
          <div className="flex flex-wrap gap-5">
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-preview-link"
              className="text-slate-400 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors"
            >
              Preview what they'll see →
            </a>
            <button
              onClick={revoke}
              disabled={busy}
              data-testid="share-revoke-btn"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-red-400 text-xs uppercase tracking-widest transition-colors disabled:opacity-50"
            >
              <XCircle size={13} /> Revoke link
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-5">
          <button
            onClick={() => createLink(includeStrategies)}
            disabled={busy}
            data-testid="report-share-btn"
            className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Share2 size={13} />}
            {report.share_active && !revoked ? "Get my share link" : "Create share link"}
          </button>
          {revoked && (
            <span className="text-slate-500 text-xs uppercase tracking-widest" data-testid="share-revoked-note">
              Previous link revoked
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const CohortMatchPanel = ({ match, premium }) => {
  if (!match) return null;
  const profile = match.cohort_profile || {};
  const strategies = match.strategies || [];
  const locked = match.strategies_locked || 0;
  const topBehaviours = profile.top_behaviours || [];
  const closest = match.closest_match_percent;
  const dataset = (match.dataset_size || 0).toLocaleString();

  return (
    <div className="border border-[#D4AF37]/25 bg-[#0B1220]/80" data-testid="report-cohort-match">
      <div className="p-6 md:p-8 border-b border-slate-800">
        <div className="flex items-center gap-2.5 mb-3">
          <Users size={15} className="text-[#D4AF37]" />
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37]">Your closest cases in the BADBOSS survey</p>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
          {closest ? `${closest}% match` : "Cohort matched"}{" "}
          <span className="text-slate-500 text-lg">to your closest documented case</span>
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed max-w-3xl">
          We matched your leader's dark triad profile and the behaviours you logged against{" "}
          <span className="text-white font-medium">{dataset} anonymised cases</span> from the clinic's global BADBOSS
          survey. The nearest {match.cohort_size} cases span {profile.countries || 0} countries — {match.accounts_analysed || 0}{" "}
          of them describe what the respondent actually did about it.
        </p>
      </div>

      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-800">
        <div className="p-6" data-testid="cohort-stat-match">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Closest case</p>
          <p className="font-serif text-3xl text-[#D4AF37]">{closest != null ? `${closest}%` : "—"}</p>
          <p className="text-slate-500 text-xs mt-1">similarity to yours</p>
        </div>
        <div className="p-6" data-testid="cohort-stat-size">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Cohort size</p>
          <p className="font-serif text-3xl text-white">{match.cohort_size || 0}</p>
          <p className="text-slate-500 text-xs mt-1">nearest cases analysed</p>
        </div>
        <div className="p-6" data-testid="cohort-stat-dataset">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Dataset</p>
          <p className="font-serif text-3xl text-white">{dataset}</p>
          <p className="text-slate-500 text-xs mt-1">documented cases</p>
        </div>
      </div>

      {(profile.narcissism || topBehaviours.length > 0) && (
        <div className="grid md:grid-cols-2 gap-0 md:gap-0 border-t border-slate-800">
          {profile.narcissism && (
            <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-slate-800" data-testid="cohort-leader-profile">
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">Cohort leader averages (1–7)</p>
              <div className="space-y-4">
                {[
                  { key: "narcissism", label: "Narcissism" },
                  { key: "machiavellianism", label: "Machiavellianism" },
                  { key: "psychopathy", label: "Psychopathy" },
                ].map((t) => {
                  const val = profile[t.key];
                  const pct = val ? (val / 7) * 100 : 0;
                  return (
                    <div key={t.key}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-slate-300">{t.label}</span>
                        <span className="font-mono text-slate-400">{val ?? "—"}</span>
                      </div>
                      <div className="h-1.5 bg-slate-800">
                        <div
                          className="h-full bg-[#FB923C]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {topBehaviours.length > 0 && (
            <div className="p-6 md:p-8" data-testid="cohort-top-behaviours">
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">Most common behaviours in your cohort</p>
              <div className="space-y-3">
                {topBehaviours.map((b) => (
                  <div key={b.dimension} className="flex items-center gap-4">
                    <span className="text-slate-300 text-sm flex-1">{b.label}</span>
                    <div className="w-24 h-1.5 bg-slate-800">
                      <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, b.share)}%` }} />
                    </div>
                    <span className="font-mono text-xs text-slate-400 w-10 text-right">{b.share}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {match.cohort_summary && (
        <div className="p-6 md:p-8 border-t border-slate-800" data-testid="cohort-summary">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">What people like you are dealing with</p>
          <p className="text-slate-300 text-sm leading-relaxed">{match.cohort_summary}</p>
        </div>
      )}

      <div className="p-6 md:p-8 border-t border-slate-800">
        <div className="flex items-center gap-2.5 mb-5">
          <Target size={15} className="text-[#D4AF37]" />
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">What worked for people matched to you</p>
        </div>

        {premium && strategies.length > 0 ? (
          <ul className="space-y-5" data-testid="cohort-strategies">
            {strategies.map((s, idx) => (
              <li key={idx} className="border-l-2 border-[#D4AF37]/50 pl-5" data-testid={`cohort-strategy-${idx}`}>
                <p className="text-white font-medium text-sm mb-1.5">{s.title}</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-2">{s.action}</p>
                {s.evidence && <p className="text-slate-500 text-xs italic mb-1.5">{s.evidence}</p>}
                {s.prevalence && (
                  <p className="text-slate-500 text-[11px] uppercase tracking-wider">In your cohort: {s.prevalence}</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 border border-slate-800 p-5"
            data-testid="cohort-strategies-locked"
          >
            <p className="flex items-center gap-2.5 text-slate-400 text-sm">
              <Lock size={14} className="text-[#D4AF37]" />
              {locked || strategies.length || 3} evidence-based strategies from the matched cohort — Premium unlocks the wording, cadence and what to record
            </p>
            <Link
              to="/portal/pricing"
              className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] hover:underline shrink-0"
              data-testid="cohort-strategies-upgrade"
            >
              Unlock →
            </Link>
          </div>
        )}

        {premium && match.avoid && (
          <div className="mt-6 border border-red-500/25 bg-red-500/[0.05] p-5" data-testid="cohort-avoid">
            <div className="flex items-center gap-2 mb-2">
              <AlertOctagon size={13} className="text-red-400" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-red-400">What made things worse</p>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">{match.avoid}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function VulnerabilityReportPage() {
  const { user } = usePortalAuth();
  const [report, setReport] = useState(null);
  const [trends, setTrends] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, t, i] = await Promise.all([portalApi.latestReport(), portalApi.trends(), portalApi.incidents()]);
        setReport(r);
        setTrends(t);
        setIncidents(i);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const generate = async (regenerate = false) => {
    setGenerating(true);
    try {
      const fresh = await portalApi.generateReport(regenerate);
      setReport(fresh);
      track("report_generated", { regenerate });
      if (fresh.emailed_at) toast.success("Report emailed to you as a PDF");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const res = await portalApi.emailReport();
      toast.success(res.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEmailing(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      await downloadReportPdf();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const prerequisites = [
    { label: "Big Five assessment complete", done: !!user?.big_five_completed, to: "/portal/big-five" },
    { label: "At least one incident logged", done: incidents.length > 0, to: "/portal/incident/new" },
    {
      label: "Leader D3 assessment complete",
      done: incidents.some((i) => i.dark_triad_scores),
      to: incidents.length ? `/portal/incident/${incidents[0].incident_id}/leader-d3` : "/portal/incident/new",
    },
  ];
  const ready = prerequisites.every((p) => p.done);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex justify-center py-24" data-testid="report-loading">
          <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div data-testid="vulnerability-report-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Analysis</p>
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-3">Your vulnerability report</h1>
            <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">
              Where your traits and your leader's traits collide — ranked by how much damage each interaction can do.
            </p>
          </div>
          {report && (
            <div className="flex flex-wrap gap-3 shrink-0">
              <button
                onClick={sendEmail}
                disabled={emailing}
                data-testid="report-email-btn"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-50 transition-colors"
              >
                {emailing ? <Loader2 className="animate-spin" size={13} /> : <Mail size={13} />}
                {emailing ? "Sending" : "Email me this report"}
              </button>
              <button
                onClick={download}
                disabled={downloading}
                data-testid="report-download-btn"
                className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
              >
                {downloading ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />}
                PDF
              </button>
              <button
                onClick={() => generate(true)}
                disabled={generating}
                data-testid="report-regenerate-btn"
                className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
              >
                {generating ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />}
                Regenerate
              </button>
            </div>
          )}
        </div>

        {!report ? (
          <div className="border border-slate-800 bg-[#0B1220]/80 p-7 md:p-10 max-w-2xl" data-testid="report-empty-state">
            <ShieldAlert className="text-[#D4AF37] mb-6" size={30} />
            <h2 className="font-serif text-2xl text-white mb-3">Three inputs, then your report</h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              The analysis pairs each of your Big Five traits against your leader's dark triad scores and cross-references
              your logged incidents.
            </p>
            <ul className="space-y-4 mb-9">
              {prerequisites.map((p) => (
                <li key={p.label} className="flex items-center gap-3">
                  <span
                    className={`w-5 h-5 flex items-center justify-center border ${
                      p.done ? "border-[#D4AF37] bg-[#D4AF37]/15" : "border-slate-700"
                    }`}
                  >
                    {p.done && <Check size={12} className="text-[#D4AF37]" />}
                  </span>
                  {p.done ? (
                    <span className="text-slate-300 text-sm">{p.label}</span>
                  ) : (
                    <Link to={p.to} className="text-slate-400 text-sm hover:text-[#D4AF37] transition-colors">
                      {p.label} →
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <button
              onClick={() => generate(false)}
              disabled={!ready || generating}
              data-testid="report-generate-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-40 transition-colors"
            >
              {generating && <Loader2 className="animate-spin" size={14} />}
              {generating ? "Analysing your profile" : "Generate my report"}
            </button>
            {generating && (
              <p className="text-slate-500 text-xs mt-4" data-testid="report-generating-note">
                This takes up to a minute — we are reading every incident against both profiles.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-10">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8" data-testid="report-big-five">
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Your profile</p>
                <div className="space-y-5">
                  {BIG_FIVE_TRAITS.map((t) => (
                    <TraitBar key={t} label={TRAIT_LABELS[t]} value={report.big_five_scores?.[t] ?? 0} />
                  ))}
                </div>
              </div>
              <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8" data-testid="report-dark-triad">
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Your leader's profile</p>
                <div className="space-y-5">
                  {DARK_TRIAD_TRAITS.map((t) => (
                    <TraitBar
                      key={t}
                      label={TRAIT_LABELS[t]}
                      value={report.dark_triad_scores?.[t] ?? 0}
                      color={(report.dark_triad_scores?.[t] ?? 0) >= 60 ? "#F87171" : "#FB923C"}
                    />
                  ))}
                </div>
                <p className="text-slate-600 text-xs mt-7 leading-relaxed">
                  Averaged across {report.incident_count} logged {report.incident_count === 1 ? "incident" : "incidents"}.
                </p>
              </div>
            </div>

            {report.escalation_reason && (
              <div className="border border-red-500/30 bg-red-500/[0.06] p-6 md:p-8" data-testid="report-escalation">
                <div className="flex items-center gap-2.5 mb-4">
                  <AlertTriangle size={15} className="text-red-400" />
                  <p className="text-[11px] uppercase tracking-[0.24em] text-red-400">Escalation risk</p>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{report.escalation_reason}</p>
              </div>
            )}

            <div>
              <h2 className="font-serif text-2xl text-white mb-6">
                {report.vulnerabilities.length} exploitable trait interactions
              </h2>
              <div className="space-y-5">
                {report.vulnerabilities.map((v, idx) => (
                  <VulnerabilityCard key={v.id || idx} vuln={v} index={idx} premium={report.premium} />
                ))}
              </div>
            </div>

            {report.dataset_match && (
              <CohortMatchPanel match={report.dataset_match} premium={report.premium} />
            )}

            {trends && <TrendChart trends={trends} />}

            <SharePanel report={report} premium={report.premium} />

            {!report.premium && (
              <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-7 md:p-10" data-testid="report-premium-cta">
                <h3 className="font-serif text-2xl text-white mb-3">Unlock your counter-strategies</h3>
                <p className="text-slate-400 text-sm max-w-xl leading-relaxed mb-7">
                  Premium turns each vulnerability into a specific plan — the wording to use, what to record, and what to
                  do if it escalates. Plus unlimited incident logging.
                </p>
                <Link
                  to="/portal/pricing"
                  className="inline-block bg-[#D4AF37] text-slate-950 px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
                  data-testid="report-see-plans-btn"
                >
                  See plans
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
